<template lang="html">
  <div class="box-details">
    <div class="has-text-centered">
      <span
        v-if="boxSetInfo === null && loading > 0"
        class="icon has-text-primary is-large"
      >
        <i class="las la-2x la-sync fa-spin"></i>
      </span>
    </div>
    <cw-box-info
      :class="{
        'detail-card box is-floating': displayAsCard,
      }"
      v-if="boxSetInfo !== null"
      :boxSetInfo="boxSetInfo"
    />
    <section
      class="featured-buttons"
      v-if="canCreateOrders && boxSetInfo && !hasPendingService"
    >
      <div class="columns is-mobile">
        <a class="column is-third" @click="requestTripType('pull')">
          <div class="has-text-centered heading box is-floating">
            <icon name="pullSimple" scale="3" class="has-text-primary"></icon>
            <p>Pull</p>
          </div>
        </a>
        <a class="column is-third" @click="requestTripType('respot')">
          <div class="has-text-centered heading box is-floating">
            <icon name="respotSimple" scale="3" class="has-text-primary"></icon>
            <p>Respot</p>
          </div>
        </a>
        <a class="column is-third" @click="requestTripType('roundtrip')">
          <div class="has-text-centered heading box is-floating">
            <icon
              name="roundtripSimple"
              scale="3"
              class="has-text-primary"
            ></icon>
            <p>Roundtrip</p>
          </div>
        </a>
      </div>
    </section>
    <p
      class="has-text-centered is-size-4 has-text-primary is-padded-large"
      v-else-if="boxSetInfo !== null && pendingTripType && !isOptimisticDelayed"
    >
      Pending {{ pendingTripType }}...
    </p>
    <p
      class="has-text-centered is-size-5 has-text-grey is-padded-large"
      v-else-if="boxSetInfo !== null && pendingTripType && isOptimisticDelayed"
    >
      Still confirming your {{ pendingTripType }} request. This is taking longer
      than usual - check back shortly.
    </p>
  </div>
</template>

<script>
import {
  DISPATCH_SET_ORDER_DETAILS_QUERY,
  STAGED_ORDER_WORK_ORDER,
} from '@/graphql/constants';
import authRequired from '@/components/mixins/auth-required';
import autoRefresh from '@/components/mixins/auto-refresh';
import permissionsRequired from '@/components/mixins/permissions-required';
import screenshotConfig from '@/components/mixins/screenshot-config';
import boxStatus from '@/enums/box-status';
import {
  OPTIMISTIC_POLL_INTERVAL_MS,
  OPTIMISTIC_MAX_POLL_ATTEMPTS,
} from '@/store/modules/orders';

// FOR SCREENSHOTS
import Mock from '@/mock-data/screenshots';

export default {
  name: 'box-details',
  // share common functionality with component mixins
  mixins: [authRequired, autoRefresh, permissionsRequired, screenshotConfig],
  props: {
    displayAsCard: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  // variables
  data() {
    return {
      loading: 0,
      boxSetInfo: null,
      // Bumped on every active reconciliation poll so isOptimisticDelayed can
      // react to time passing without needing a separate render timer.
      nowTick: Date.now(),
    };
  },
  apollo: {
    boxSetInfo: {
      query: DISPATCH_SET_ORDER_DETAILS_QUERY,
      variables() {
        return { workOrderId: this.$route.params.boxid };
      },
      update(data) {
        if (this.capturingScreenshots) {
          return Mock.currentDispatchSets[0];
        }
        // The returned value will update
        // the vue property 'boxSetInfo'
        return data.dispatchSet;
      },
    },
  },
  computed: {
    // Read optimistic box state from Vuex so it persists across navigation.
    optimisticBoxState() {
      const workOrderId = this.$route.params.boxid;
      return this.$store.state.orders.optimisticBoxStates[workOrderId] || null;
    },
    optimisticTripType() {
      return (
        (this.optimisticBoxState && this.optimisticBoxState.tripType) || null
      );
    },
    // True once we've relied on the optimistic guess alone for at least one
    // full reconciliation interval, so the UI can stop implying certainty
    // while still telling the user something is happening. Deliberately
    // coarse (see OPTIMISTIC_POLL_INTERVAL_MS) rather than a live countdown.
    isOptimisticDelayed() {
      if (!this.optimisticBoxState) {
        return false;
      }
      return (
        this.nowTick - this.optimisticBoxState.setAt >=
        OPTIMISTIC_POLL_INTERVAL_MS
      );
    },
    project() {
      return (
        (this.boxSetInfo && this.boxSetInfo.project) || {
          name: '',
          address: { street: '' },
        }
      );
    },
    pendingTripType() {
      if (this.boxSetInfo && this.boxSetInfo.dispatchPull) {
        return this.boxSetInfo.dispatchPull.tripType;
      }
      // Fall back to the staged order's trip type (real or optimistic) so the
      // "Pending ..." message renders as soon as an order is placed, before a
      // dispatchPull record has been created.
      if (this.stagedOrder) {
        return this.stagedOrder.tripType;
      }
      return null;
    },
    stagedOrder() {
      const real =
        (this.boxSetInfo &&
          this.boxSetInfo.stagedOrder &&
          this.boxSetInfo.stagedOrder.tripType !== 'set' &&
          this.boxSetInfo.stagedOrder) ||
        null;
      if (real) {
        return real;
      }
      // Optimistic fallback: mirror the DB staged-order shape so hasPendingService
      // and pendingTripType behave exactly as if the order were already written.
      if (this.optimisticTripType) {
        return { tripType: this.optimisticTripType };
      }
      return null;
    },
    hasPendingService() {
      if (this.stagedOrder) {
        return true;
      }

      switch (this.boxSetInfo.status) {
        // Service pending for any of these box statuses
        case boxStatus.setCreated:
        case boxStatus.setCWD:
        case boxStatus.pullCreated:
        case boxStatus.pullCWD:
        case boxStatus.poHold:
        case boxStatus.redMobileNew:
          return true;

        // Service is not pending for these box statuses
        case boxStatus.setManual:
        case boxStatus.boxSet:
        case boxStatus.complete:
        case boxStatus.pullManual:
          return false;

        // Service is pending if there is no DriverPull Record
        case boxStatus.poReleased:
          return !this.boxSetInfo.driverPullId;
        default:
      }
      return false;
    },
  },
  components: {},
  // methods
  watch: {
    boxSetInfo(newVal) {
      // Once the DB catches up and the real staged order is present, drop the
      // optimistic override so the server data drives the UI going forward.
      if (newVal && newVal.stagedOrder) {
        const workOrderId = this.$route.params.boxid;
        this.$store.commit('orders/clearOptimisticBoxState', workOrderId);
      }
    },
  },
  methods: {
    requestTripType(type) {
      let boxId = this.$route.params.boxid;
      this.$modal.show('PullRequest', { boxId, type });
    },
    normalizeTripType(type) {
      if (!type || typeof type !== 'string') {
        return null;
      }
      return type.charAt(0).toUpperCase() + type.slice(1);
    },
    handleOrderSubmitted({ workOrderId, success, type }) {
      if (workOrderId !== this.$route.params.boxid) {
        return;
      }
      // After a successful submission the order is sitting in the SQS queue
      // and has not yet been written to FileMaker. Refetching now would
      // return stale data and make it look like the order never went
      // through, so instead we optimistically render the box as if the
      // staged order already exists, and actively (but sparingly) re-check
      // in the background until it's confirmed instead of trusting the
      // guess indefinitely.
      if (success) {
        const normalizedType = this.normalizeTripType(type);
        this.$store.commit('orders/setOptimisticBoxState', {
          workOrderId,
          tripType: normalizedType,
        });
        this.startReconcilePolling(workOrderId);
      }
    },
    startReconcilePolling(workOrderId) {
      this.stopReconcilePolling();
      this._reconcileAttempts = 0;
      // Spaced out in minutes, not seconds: FileMaker writes go through a
      // queue consumer that can legitimately take a while (see
      // server/sqs-queue/consumer.js), and the FM database is a shared,
      // sometimes-taxed resource, so tight polling would add load without
      // being able to resolve any faster than the backend actually writes.
      this._reconcileInterval = setInterval(() => {
        this.pollForReconciliation(workOrderId);
      }, OPTIMISTIC_POLL_INTERVAL_MS);
    },
    stopReconcilePolling() {
      if (this._reconcileInterval) {
        clearInterval(this._reconcileInterval);
        this._reconcileInterval = null;
      }
    },
    async pollForReconciliation(workOrderId) {
      this.nowTick = Date.now();
      this._reconcileAttempts += 1;
      const stillOptimistic = !!this.$store.state.orders.optimisticBoxStates[
        workOrderId
      ];
      const stillViewingBox = workOrderId === this.$route.params.boxid;
      if (
        !stillOptimistic ||
        !stillViewingBox ||
        this._reconcileAttempts > OPTIMISTIC_MAX_POLL_ATTEMPTS
      ) {
        this.stopReconcilePolling();
        return;
      }
      try {
        // A cheap existence probe instead of re-running the full, heavier
        // boxSetInfo query just to check whether anything changed yet.
        const { data } = await this.$apollo.query({
          query: STAGED_ORDER_WORK_ORDER,
          variables: { workOrderId },
          fetchPolicy: 'network-only',
        });
        if (data && data.stagedOrder) {
          this.stopReconcilePolling();
          if (this.$apollo.queries.boxSetInfo) {
            this.$apollo.queries.boxSetInfo.refetch();
          }
        }
      } catch (err) {
        // Transient network/FM hiccup - leave the optimistic state in place
        // and try again on the next tick rather than failing loudly.
        window.console.error('Error polling for order reconciliation', err);
      }
    },
  },
  mounted() {
    this._orderSubmittedHandler = this.handleOrderSubmitted;
    this.$broadcast().$on('order-submitted', this._orderSubmittedHandler);

    // Resume active re-checking if we still have an outstanding optimistic
    // guess for this exact box - e.g. the user navigated away and back
    // before it was confirmed. Without this it would sit until the next
    // remount or the 20-minute global refresh.
    const workOrderId = this.$route.params.boxid;
    if (this.$store.state.orders.optimisticBoxStates[workOrderId]) {
      this.startReconcilePolling(workOrderId);
    }
  },
  beforeDestroy() {
    this.stopReconcilePolling();
    if (this._orderSubmittedHandler) {
      this.$broadcast().$off('order-submitted', this._orderSubmittedHandler);
    }
  },
};
</script>

<style lang="stylus" scoped></style>
